import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Brain, Target, TrendingUp, Calendar, CheckCircle2, Clock, BookOpen,
  Zap, Award, ChevronRight, Play, BarChart3, Lightbulb, Loader2,
  Star, Trophy, Sparkles, GraduationCap, Rocket, ArrowRight, RefreshCw,
  Library, MessageCircle, PenTool, BookMarked, ChevronLeft, Home,
  CircleDot, Circle, Check, X, HelpCircle, Flag, Flame, Medal, Crown,
  Gift, TrendingDown, ArrowLeft
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Year group to stage mapping
const YEAR_TO_STAGE = {
  'year-1': 'ks1', 'year-2': 'ks1',
  'year-3': 'ks2-lower', 'year-4': 'ks2-lower',
  'year-5': 'ks2-upper', 'year-6': 'ks2-upper',
  'year-7': 'ks3', 'year-8': 'ks3', 'year-9': 'ks3',
  'year-10': 'ks4', 'year-11': 'ks4',
  'year-12': 'a-level', 'year-13': 'a-level',
  '7-plus': '7-plus', '11-plus': '11-plus',
  'gcse': 'ks4', 'a-level': 'a-level'
};

// Helper to get stage from year group
const getStageFromYearGroup = (yearGroup) => {
  if (!yearGroup) return 'ks3'; // Default to KS3
  const normalized = yearGroup.toLowerCase().replace(/\s+/g, '-');
  return YEAR_TO_STAGE[normalized] || 'ks3';
};

// Learning Modes
const LEARNING_MODES = {
  exam: {
    id: 'exam',
    name: 'Exam Target Mode',
    icon: Target,
    color: 'from-purple-600 to-pink-600',
    description: 'Prepare for specific exams like 11+, GCSE, A-Levels',
    features: ['Exam-aligned syllabus', 'Mock exam practice', 'Performance predictions', 'Time management training']
  },
  skill: {
    id: 'skill',
    name: 'Skill Building Mode',
    icon: Brain,
    color: 'from-blue-600 to-cyan-600',
    description: 'Focus on building strong foundations and mastering concepts',
    features: ['Curriculum-aligned content', 'Concept mastery', 'Progress tracking', 'Adaptive difficulty']
  }
};

// Phase indicators
const PHASES = [
  { id: 'discover', name: 'Discover', icon: Sparkles, description: 'Set your starting point' },
  { id: 'plan', name: 'Plan', icon: Calendar, description: 'Your personalized roadmap' },
  { id: 'learn', name: 'Learn', icon: BookOpen, description: 'Daily micro-learning' },
  { id: 'assess', name: 'Assess', icon: Target, description: 'Weekly adaptive check' }
];

// Capability bands
// Revised banding system based on assessment scores:
// 0-49%: Developing - Building foundational understanding
// 50-69%: Secure - Core concepts understood  
// 70-84%: Proficient - Strong grasp of material
// 85-100%: Advanced - Deep understanding & mastery
const BAND_CONFIG = {
  developing: { color: 'bg-orange-500', text: 'text-orange-400', label: 'Developing', description: '0-49%: Building foundational understanding' },
  secure: { color: 'bg-green-500', text: 'text-green-400', label: 'Secure', description: '50-69%: Core concepts understood' },
  proficient: { color: 'bg-blue-500', text: 'text-blue-400', label: 'Proficient', description: '70-84%: Strong grasp of material' },
  advanced: { color: 'bg-purple-500', text: 'text-purple-400', label: 'Advanced', description: '85-100%: Deep understanding & mastery' }
};

const StudentJourney = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  
  // Core state
  const [loading, setLoading] = useState(true);
  const [phase, setPhase] = useState('discover'); // discover, plan, learn, assess
  const [view, setView] = useState('loading'); // loading, goal-select, diagnostic, results, dashboard, lesson, assessment
  
  // User & Journey state
  const [user, setUser] = useState(null);
  const [learningMode, setLearningMode] = useState(null); // 'exam' or 'skill'
  const [journeyData, setJourneyData] = useState(null);
  const [weeklyPlan, setWeeklyPlan] = useState(null);
  const [currentWeek, setCurrentWeek] = useState(1);
  
  // Diagnostic state
  const [diagnosticSession, setDiagnosticSession] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [diagnosticProgress, setDiagnosticProgress] = useState(0);
  const [diagnosticResults, setDiagnosticResults] = useState(null);
  
  // Lesson state
  const [currentLesson, setCurrentLesson] = useState(null);
  const [lessonTab, setLessonTab] = useState('learn');
  const [practiceAnswers, setPracticeAnswers] = useState({});
  const [practiceSubmitted, setPracticeSubmitted] = useState(false);
  
  // Assessment state
  const [assessmentData, setAssessmentData] = useState(null);
  const [assessmentAnswers, setAssessmentAnswers] = useState({});
  const [assessmentIndex, setAssessmentIndex] = useState(0);
  const [assessmentTime, setAssessmentTime] = useState(0);
  const [assessmentResults, setAssessmentResults] = useState(null);
  const [isAssessmentTimerRunning, setIsAssessmentTimerRunning] = useState(false);
  
  // Gamification state
  const [gamificationProfile, setGamificationProfile] = useState(null);
  const [showBadgeModal, setShowBadgeModal] = useState(false);
  const [newBadges, setNewBadges] = useState([]);
  const [showLevelUp, setShowLevelUp] = useState(false);
  const [newLevel, setNewLevel] = useState(null);
  
  // Selected day for weekly view
  const [selectedDay, setSelectedDay] = useState('monday');

  // Assessment Timer Effect
  useEffect(() => {
    let interval;
    if (isAssessmentTimerRunning && assessmentTime > 0) {
      interval = setInterval(() => {
        setAssessmentTime(prev => {
          if (prev <= 1) {
            setIsAssessmentTimerRunning(false);
            // Auto-submit when time runs out
            submitAssessment();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isAssessmentTimerRunning]);

  useEffect(() => {
    if (!token) {
      navigate('/auth');
      return;
    }
    initializeJourney();
  }, [token]);

  // Fetch gamification profile
  const fetchGamificationProfile = async () => {
    try {
      const res = await axios.get(`${API}/gamification/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGamificationProfile(res.data);
      
      // Check streak on load
      await axios.post(`${API}/gamification/check-streak`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Error fetching gamification profile:', error);
    }
  };

  // Award XP for an action
  const awardXP = async (action, context = {}) => {
    try {
      const res = await axios.post(`${API}/gamification/award-xp`, {
        action,
        context
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update profile
      setGamificationProfile(prev => ({
        ...prev,
        xp: { ...prev?.xp, total: res.data.new_total_xp }
      }));
      
      // Show XP toast
      toast.success(`+${res.data.xp_earned.total} XP!`, {
        description: res.data.xp_earned.reasons.join(' ')
      });
      
      // Handle new badges
      if (res.data.new_badges?.length > 0) {
        setNewBadges(res.data.new_badges);
        setShowBadgeModal(true);
      }
      
      // Handle level up
      if (res.data.level_up?.occurred) {
        setNewLevel(res.data.level_up.new_level);
        setShowLevelUp(true);
        setTimeout(() => setShowLevelUp(false), 3000);
      }
      
      // Refresh profile
      fetchGamificationProfile();
      
      return res.data;
    } catch (error) {
      console.error('Error awarding XP:', error);
      return null;
    }
  };

  const initializeJourney = async () => {
    setLoading(true);
    try {
      // Get user info
      const userRes = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(userRes.data.user);
      
      // Fetch gamification profile
      await fetchGamificationProfile();
      
      // Check if user has an existing learning path
      const pathRes = await axios.get(`${API}/learning-engine/path/current`, {
        headers: { Authorization: `Bearer ${token}` }
      }).catch((err) => {
        console.error('Path fetch error:', err);
        return { data: null };
      });
      
      console.log('Path response:', pathRes.data);
      
      if (pathRes.data && pathRes.data.has_path && pathRes.data.path) {
        // User has existing journey - go to dashboard
        const path = pathRes.data.path;
        console.log('Setting journey data:', path);
        setJourneyData(path);
        setWeeklyPlan(path.weekly_schedule);
        setLearningMode(path.learning_mode || 'exam');
        setCurrentWeek(path.week_number || 1);
        setPhase('learn');
        setView('dashboard');
      } else {
        // New user - start discovery phase
        console.log('No path found, showing goal select');
        setPhase('discover');
        setView('goal-select');
      }
    } catch (error) {
      console.error('Error initializing journey:', error);
      toast.error('Failed to load your journey');
      setView('goal-select');
    } finally {
      setLoading(false);
    }
  };

  // Start diagnostic assessment
  const startDiagnostic = async () => {
    setLoading(true);
    try {
      // Determine correct stage from user's year group
      const yearGroup = user?.studentProfile?.yearGroup || user?.year_group;
      const effectiveStage = getStageFromYearGroup(yearGroup);
      
      const res = await axios.post(`${API}/learning-engine/diagnostic/start`, {
        stage: effectiveStage,
        subject: 'maths'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setDiagnosticSession(res.data);
      setCurrentQuestion(res.data.current_question);
      setDiagnosticProgress(0);
      setView('diagnostic');
    } catch (error) {
      console.error('Error starting diagnostic:', error);
      toast.error('Failed to start diagnostic');
    } finally {
      setLoading(false);
    }
  };

  // Submit diagnostic answer
  const submitDiagnosticAnswer = async () => {
    if (selectedAnswer === null) return;
    
    setLoading(true);
    try {
      const res = await axios.post(`${API}/learning-engine/diagnostic/answer`, {
        session_id: diagnosticSession.session_id,
        question_index: currentQuestion.index,
        selected_answer: selectedAnswer
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setDiagnosticProgress(res.data.progress);
      
      if (res.data.completed) {
        // Diagnostic complete - show results
        setDiagnosticResults(res.data.results);
        setView('results');
      } else {
        // Next question
        setCurrentQuestion(res.data.next_question);
        setSelectedAnswer(null);
      }
    } catch (error) {
      console.error('Error submitting answer:', error);
      toast.error('Failed to submit answer');
    } finally {
      setLoading(false);
    }
  };

  // Generate learning path after diagnostic
  const generateLearningPath = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/learning-engine/path/generate`, {
        diagnostic_session_id: diagnosticSession?.session_id,
        learning_mode: learningMode,
        stage: user?.studentProfile?.yearGroup?.includes('year-5') || user?.studentProfile?.yearGroup?.includes('year-6') ? '11-plus' : 'gcse',
        subject: 'maths'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setJourneyData(res.data.learning_path);
      setWeeklyPlan(res.data.learning_path.weekly_schedule);
      setCurrentWeek(1);
      setPhase('learn');
      setView('dashboard');
      toast.success('Your personalized learning path is ready!');
    } catch (error) {
      console.error('Error generating path:', error);
      toast.error('Failed to generate learning path');
    } finally {
      setLoading(false);
    }
  };

  // Open a lesson
  const openLesson = async (lessonId) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/learning-engine/lesson/${lessonId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCurrentLesson(res.data);
      setLessonTab('learn');
      setPracticeAnswers({});
      setPracticeSubmitted(false);
      setView('lesson');
    } catch (error) {
      console.error('Error loading lesson:', error);
      toast.error('Failed to load lesson');
    } finally {
      setLoading(false);
    }
  };

  // Submit practice answers
  const submitPractice = async () => {
    if (!currentLesson?.practice_questions) return;
    
    const results = currentLesson.practice_questions.map((q, idx) => ({
      question_id: q.id || idx,
      correct: practiceAnswers[idx] === q.correct_answer,
      user_answer: practiceAnswers[idx],
      correct_answer: q.correct_answer
    }));
    
    const score = results.filter(r => r.correct).length;
    const scorePercent = (score / results.length) * 100;
    setPracticeSubmitted(true);
    
    // Mark lesson as complete
    try {
      await axios.post(`${API}/learning-engine/lesson/complete`, {
        lesson_id: currentLesson.lesson_id,
        score: scorePercent
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Award XP for lesson completion
      const isPerfect = scorePercent === 100;
      await awardXP(isPerfect ? 'perfect_practice' : 'lesson_complete', { score: scorePercent });
      
      toast.success(`Lesson complete! Score: ${score}/${results.length}`);
    } catch (error) {
      console.error('Error completing lesson:', error);
    }
  };

  // Start weekly assessment
  const startWeeklyAssessment = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/adaptive-mock/generate`, {
        stage: journeyData?.stage || '11-plus',
        subject: journeyData?.subject || 'maths',
        focus_mode: 'balanced',
        question_count: 15,
        time_minutes: 25,
        use_ai_variation: false  // Disabled for faster response
      }, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 60000  // 60 second timeout
      });
      
      setAssessmentData(res.data);
      setAssessmentAnswers({});
      setAssessmentIndex(0);
      setAssessmentTime(res.data.duration_minutes * 60);
      setIsAssessmentTimerRunning(true); // Start the timer
      setPhase('assess');
      setView('assessment');
      toast.info('Weekly assessment started!');
    } catch (error) {
      console.error('Error starting assessment:', error);
      toast.error('Failed to start assessment');
    } finally {
      setLoading(false);
    }
  };

  // Submit weekly assessment
  const submitAssessment = async () => {
    setIsAssessmentTimerRunning(false); // Stop the timer
    setLoading(true);
    try {
      const timePerQuestion = {};
      assessmentData.questions.forEach(q => {
        timePerQuestion[q.question_id] = 60; // Approximate
      });
      
      const res = await axios.post(`${API}/adaptive-mock/submit/${assessmentData.mock_id}`, {
        mock_id: assessmentData.mock_id,
        answers: assessmentAnswers,
        time_per_question: timePerQuestion,
        flagged_questions: [],
        total_time_seconds: (assessmentData.duration_minutes * 60) - assessmentTime
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setAssessmentResults(res.data);
      
      // Award XP for assessment completion
      const score = res.data.score || 0;
      const action = score >= 80 ? 'assessment_high_score' : 'assessment_complete';
      await awardXP(action, { score });
      
      setView('assessment-results');
      toast.success('Assessment complete!');
    } catch (error) {
      console.error('Error submitting assessment:', error);
      toast.error('Failed to submit assessment');
    } finally {
      setLoading(false);
    }
  };

  // Regenerate next week's plan
  const generateNextWeek = async () => {
    setLoading(true);
    try {
      // Call the regenerate endpoint to get a new weekly schedule
      const res = await axios.post(`${API}/learning-engine/regenerate-week`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data.success) {
        toast.success(`Week ${res.data.week_number} plan generated!`);
        // Refresh the journey data
        await initializeJourney();
      }
    } catch (error) {
      console.error('Error regenerating week:', error);
      toast.error('Failed to generate next week\'s plan');
      // Continue anyway
      setPhase('learn');
      setView('dashboard');
    } finally {
      setLoading(false);
    }
  };

  // Get today's activities
  const getTodayActivities = () => {
    if (!weeklyPlan) return [];
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const today = days[new Date().getDay()];
    const dayPlan = weeklyPlan.find(d => d.day.toLowerCase() === today);
    return dayPlan?.activities || [];
  };

  // Calculate weekly progress
  const getWeeklyProgress = () => {
    if (!weeklyPlan) return 0;
    let total = 0, completed = 0;
    weeklyPlan.forEach(day => {
      day.activities?.forEach(a => {
        total++;
        if (a.completed) completed++;
      });
    });
    return total > 0 ? Math.round((completed / total) * 100) : 0;
  };

  // Check if it's assessment time (weekend) - OR allow anytime for testing
  const isAssessmentTime = () => {
    // Allow assessment anytime for better UX
    return true;
    // Original: Only weekends
    // const day = new Date().getDay();
    // return day === 0 || day === 6; // Saturday or Sunday
  };

  // ============================================
  // RENDER: Loading State
  // ============================================
  if (loading && view === 'loading') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-purple-400 mx-auto mb-4" />
          <p className="text-white/70">Preparing your learning journey...</p>
        </div>
      </div>
    );
  }

  // ============================================
  // RENDER: Phase 1 - Goal Selection
  // ============================================
  if (view === 'goal-select') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-4 md:p-8" data-testid="goal-select-view">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <Badge className="bg-purple-500/20 text-purple-300 mb-4">Phase 1: Discover</Badge>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">
              Welcome to Your Learning Journey
            </h1>
            <p className="text-gray-400 text-lg">
              Let's understand your goals and set your starting point
            </p>
          </div>

          {/* Mode Selection */}
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            {Object.values(LEARNING_MODES).map((mode) => {
              const Icon = mode.icon;
              const isSelected = learningMode === mode.id;
              return (
                <motion.div
                  key={mode.id}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Card 
                    className={`cursor-pointer transition-all border-2 ${
                      isSelected 
                        ? 'border-purple-500 bg-purple-500/10' 
                        : 'border-slate-700 bg-slate-800/50 hover:border-slate-600'
                    }`}
                    onClick={() => setLearningMode(mode.id)}
                    data-testid={`mode-${mode.id}`}
                  >
                    <CardHeader>
                      <div className="flex items-center gap-4">
                        <div className={`p-3 rounded-xl bg-gradient-to-r ${mode.color}`}>
                          <Icon className="h-6 w-6 text-white" />
                        </div>
                        <div className="flex-1">
                          <CardTitle className="text-white text-xl">{mode.name}</CardTitle>
                          <CardDescription className="text-gray-400">
                            {mode.description}
                          </CardDescription>
                        </div>
                        {isSelected && (
                          <CheckCircle2 className="h-6 w-6 text-purple-400" />
                        )}
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 gap-2">
                        {mode.features.map((feature, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-sm text-gray-300">
                            <Check className="h-4 w-4 text-green-400" />
                            {feature}
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>

          {/* Continue Button */}
          <div className="text-center">
            <Button
              onClick={startDiagnostic}
              disabled={!learningMode || loading}
              className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 px-8 py-6 text-lg"
              data-testid="start-diagnostic-btn"
            >
              {loading ? (
                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
              ) : (
                <Sparkles className="h-5 w-5 mr-2" />
              )}
              Start Quick Diagnostic (20-25 mins)
            </Button>
            <p className="text-gray-500 text-sm mt-3">
              15 questions to understand your current level
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ============================================
  // RENDER: Phase 1 - Diagnostic Assessment
  // ============================================
  if (view === 'diagnostic' && currentQuestion) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-4 md:p-8" data-testid="diagnostic-view">
        <div className="max-w-3xl mx-auto">
          {/* Progress Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-2">
              <Badge className="bg-purple-500/20 text-purple-300">Diagnostic Assessment</Badge>
              <span className="text-gray-400 text-sm">{Math.round(diagnosticProgress)}% Complete</span>
            </div>
            <Progress value={diagnosticProgress} className="h-2 bg-slate-700" />
          </div>

          {/* Question Card */}
          <Card className="bg-slate-800/70 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white text-xl leading-relaxed">
                {currentQuestion.question}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <RadioGroup 
                value={selectedAnswer?.toString()} 
                onValueChange={(v) => setSelectedAnswer(parseInt(v))}
              >
                {currentQuestion.options?.map((option, idx) => (
                  <div
                    key={idx}
                    className={`flex items-center space-x-3 p-4 rounded-lg border transition-all cursor-pointer ${
                      selectedAnswer === idx
                        ? 'border-purple-500 bg-purple-500/10'
                        : 'border-slate-600 hover:border-slate-500 bg-slate-700/30'
                    }`}
                    onClick={() => setSelectedAnswer(idx)}
                  >
                    <RadioGroupItem value={idx.toString()} id={`option-${idx}`} />
                    <Label htmlFor={`option-${idx}`} className="text-white cursor-pointer flex-1">
                      {option}
                    </Label>
                  </div>
                ))}
              </RadioGroup>

              <Button
                onClick={submitDiagnosticAnswer}
                disabled={selectedAnswer === null || loading}
                className="w-full bg-gradient-to-r from-purple-600 to-pink-600 mt-6"
                data-testid="submit-answer-btn"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <ChevronRight className="h-4 w-4 mr-2" />
                )}
                Next Question
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // ============================================
  // RENDER: Phase 1 - Diagnostic Results
  // ============================================
  if (view === 'results' && diagnosticResults) {
    const band = diagnosticResults.capability_band || 'developing';
    const bandConfig = BAND_CONFIG[band] || BAND_CONFIG.developing;
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-4 md:p-8" data-testid="results-view">
        <div className="max-w-3xl mx-auto">
          {/* Results Header */}
          <div className="text-center mb-8">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 mb-4"
            >
              <Trophy className="h-10 w-10 text-white" />
            </motion.div>
            <h1 className="text-3xl font-bold text-white mb-2">Diagnostic Complete!</h1>
            <p className="text-gray-400">Here's what we learned about your current level</p>
          </div>

          {/* Score Card */}
          <Card className="bg-slate-800/70 border-slate-700 mb-6">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Your Score</p>
                  <p className="text-4xl font-bold text-white">{diagnosticResults.score || 0}%</p>
                </div>
                <div className="text-right">
                  <p className="text-gray-400 text-sm">Current Band</p>
                  <Badge className={`${bandConfig.color} text-white px-4 py-1 text-lg`}>
                    {bandConfig.label}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Strengths & Weaknesses */}
          <div className="grid md:grid-cols-2 gap-4 mb-8">
            <Card className="bg-green-500/10 border-green-500/30">
              <CardHeader className="pb-2">
                <CardTitle className="text-green-400 text-lg flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5" /> Strengths
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {(diagnosticResults.strong_topics || ['Number operations', 'Basic algebra']).slice(0, 3).map((topic, idx) => (
                    <li key={idx} className="text-gray-300 flex items-center gap-2">
                      <Star className="h-4 w-4 text-green-400" />
                      {topic}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-orange-500/10 border-orange-500/30">
              <CardHeader className="pb-2">
                <CardTitle className="text-orange-400 text-lg flex items-center gap-2">
                  <Target className="h-5 w-5" /> Focus Areas
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {(diagnosticResults.weak_topics || ['Fractions', 'Word problems']).slice(0, 3).map((topic, idx) => (
                    <li key={idx} className="text-gray-300 flex items-center gap-2">
                      <Zap className="h-4 w-4 text-orange-400" />
                      {topic}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>

          {/* Generate Plan Button */}
          <div className="text-center">
            <Button
              onClick={generateLearningPath}
              disabled={loading}
              className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 px-8 py-6 text-lg"
              data-testid="generate-plan-btn"
            >
              {loading ? (
                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
              ) : (
                <Rocket className="h-5 w-5 mr-2" />
              )}
              Generate My Learning Plan
            </Button>
            <p className="text-gray-500 text-sm mt-3">
              AI will create a personalized weekly plan just for you
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ============================================
  // RENDER: Phase 3 - Main Dashboard
  // ============================================
  if (view === 'dashboard') {
    const todayActivities = getTodayActivities();
    const weekProgress = getWeeklyProgress();
    const showAssessment = isAssessmentTime();
    const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
    
    // Gamification data
    const xp = gamificationProfile?.xp || { total: 0, this_week: 0, to_next_level: 100 };
    const level = gamificationProfile?.level?.current || { level: 1, name: 'Newcomer', icon: '🌱' };
    const nextLevel = gamificationProfile?.level?.next;
    const levelProgress = gamificationProfile?.level?.progress || 0;
    const streak = gamificationProfile?.streak || { current: 0, longest: 0 };
    const badges = gamificationProfile?.badges?.earned || [];
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900" data-testid="dashboard-view">
        {/* Level Up Animation */}
        <AnimatePresence>
          {showLevelUp && newLevel && (
            <motion.div 
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.5 }}
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/70"
            >
              <div className="text-center">
                <motion.div
                  initial={{ rotate: 0 }}
                  animate={{ rotate: 360 }}
                  transition={{ duration: 0.5 }}
                  className="text-8xl mb-4"
                >
                  {newLevel.icon}
                </motion.div>
                <h2 className="text-4xl font-bold text-white mb-2">Level Up!</h2>
                <p className="text-2xl text-purple-400">You're now a {newLevel.name}!</p>
                <p className="text-gray-400 mt-2">Level {newLevel.level}</p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Badge Unlock Modal */}
        <AnimatePresence>
          {showBadgeModal && newBadges.length > 0 && (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
              onClick={() => setShowBadgeModal(false)}
            >
              <motion.div
                initial={{ scale: 0.5, y: 50 }}
                animate={{ scale: 1, y: 0 }}
                className="bg-slate-800 border border-purple-500 rounded-2xl p-6 max-w-md"
                onClick={e => e.stopPropagation()}
              >
                <div className="text-center mb-4">
                  <Gift className="h-12 w-12 text-purple-400 mx-auto mb-2" />
                  <h3 className="text-2xl font-bold text-white">New Badge{newBadges.length > 1 ? 's' : ''} Unlocked!</h3>
                </div>
                <div className="space-y-3">
                  {newBadges.map((badge, idx) => (
                    <div key={idx} className="flex items-center gap-4 p-3 bg-purple-500/20 rounded-lg">
                      <span className="text-3xl">{badge.icon}</span>
                      <div>
                        <p className="font-semibold text-white">{badge.name}</p>
                        <p className="text-sm text-gray-400">+{badge.xp_bonus} XP Bonus</p>
                      </div>
                    </div>
                  ))}
                </div>
                <Button 
                  onClick={() => setShowBadgeModal(false)}
                  className="w-full mt-4 bg-purple-600 hover:bg-purple-700"
                >
                  Awesome!
                </Button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 via-pink-600 to-rose-600 py-4 px-4 md:px-8">
          <div className="max-w-6xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Button variant="ghost" onClick={() => navigate(-1)} className="text-white/80 hover:text-white hover:bg-white/10" data-testid="back-btn">
                <ArrowLeft className="h-4 w-4 mr-1" /> Back
              </Button>
              <Button variant="ghost" onClick={() => navigate('/categories')} className="text-white/80 hover:text-white hover:bg-white/10">
                <Home className="h-4 w-4 mr-1" /> Home
              </Button>
            </div>
            <div className="flex items-center gap-2">
              <Badge className="bg-white/20 text-white">Week {currentWeek}</Badge>
              <Badge className={`${BAND_CONFIG[journeyData?.current_band]?.color || 'bg-purple-500'} text-white`}>
                {BAND_CONFIG[journeyData?.current_band]?.label || 'Developing'}
              </Badge>
            </div>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-4 md:px-8 py-6 space-y-6">
          {/* Gamification Header Card */}
          <Card className="bg-gradient-to-r from-slate-800/80 to-purple-900/50 border-slate-700 overflow-hidden">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
                {/* Level & XP */}
                <div className="flex items-center gap-4 flex-1">
                  <div className="relative">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center text-3xl shadow-lg shadow-purple-500/30">
                      {level.icon}
                    </div>
                    <div className="absolute -bottom-1 -right-1 bg-purple-600 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                      Lv.{level.level}
                    </div>
                  </div>
                  <div className="flex-1">
                    <p className="text-white font-semibold text-lg">{level.name}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Progress value={levelProgress} className="h-2 flex-1 bg-slate-700" />
                      <span className="text-xs text-gray-400">{xp.total} XP</span>
                    </div>
                    {nextLevel && (
                      <p className="text-xs text-purple-400 mt-1">
                        {xp.to_next_level} XP to {nextLevel.name}
                      </p>
                    )}
                  </div>
                </div>

                {/* Streak */}
                <div className="flex items-center gap-3 px-4 py-3 bg-orange-500/10 rounded-xl border border-orange-500/30">
                  <Flame className="h-8 w-8 text-orange-500" />
                  <div>
                    <p className="text-2xl font-bold text-white">{streak.current}</p>
                    <p className="text-xs text-orange-400">Day Streak</p>
                  </div>
                </div>

                {/* Badges Count */}
                <div className="flex items-center gap-3 px-4 py-3 bg-yellow-500/10 rounded-xl border border-yellow-500/30">
                  <Trophy className="h-8 w-8 text-yellow-500" />
                  <div>
                    <p className="text-2xl font-bold text-white">{badges.length}</p>
                    <p className="text-xs text-yellow-400">Badges</p>
                  </div>
                </div>
              </div>

              {/* Recent Badges */}
              {badges.length > 0 && (
                <div className="mt-4 pt-4 border-t border-slate-700">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm text-gray-400">Recent Badges</p>
                    <Button variant="link" className="text-purple-400 text-sm p-0 h-auto" onClick={() => navigate('/badges')}>
                      View All
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {badges.slice(0, 5).map((badge, idx) => (
                      <div 
                        key={idx}
                        className="flex items-center gap-2 px-3 py-1.5 bg-slate-700/50 rounded-full"
                        title={badge.description}
                      >
                        <span className="text-lg">{badge.icon}</span>
                        <span className="text-sm text-gray-300">{badge.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Band Progress Card */}
          <Card className="bg-gradient-to-r from-slate-800/80 to-slate-900/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-white font-semibold flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-purple-400" />
                  Band Progress
                </h3>
                <Badge className={`${BAND_CONFIG[journeyData?.current_band]?.color || 'bg-orange-500'} text-white`}>
                  {BAND_CONFIG[journeyData?.current_band]?.label || 'Developing'}
                </Badge>
              </div>
              
              {/* Band Progress Visualization */}
              <div className="relative">
                {/* Progress Track */}
                <div className="flex items-center gap-1 mb-2">
                  {['developing', 'secure', 'proficient', 'advanced'].map((band, idx) => {
                    const currentBandIndex = ['developing', 'secure', 'proficient', 'advanced'].indexOf(journeyData?.current_band || 'developing');
                    const isCompleted = idx < currentBandIndex;
                    const isCurrent = idx === currentBandIndex;
                    const bandColors = {
                      developing: 'bg-orange-500',
                      secure: 'bg-green-500',
                      proficient: 'bg-blue-500',
                      advanced: 'bg-purple-500'
                    };
                    
                    return (
                      <div key={band} className="flex-1 flex flex-col items-center">
                        <div className={`h-2 w-full rounded-full ${
                          isCompleted || isCurrent ? bandColors[band] : 'bg-slate-700'
                        } ${isCurrent ? 'ring-2 ring-white/30' : ''}`} />
                        <span className={`text-xs mt-1 ${
                          isCurrent ? 'text-white font-medium' : 'text-slate-500'
                        }`}>
                          {band.charAt(0).toUpperCase() + band.slice(1)}
                        </span>
                      </div>
                    );
                  })}
                </div>
                
                {/* Next Band Info */}
                {journeyData?.current_band !== 'advanced' && (
                  <div className="mt-3 p-2 bg-slate-700/50 rounded-lg">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-400">
                        {(() => {
                          const bands = ['developing', 'secure', 'proficient', 'advanced'];
                          const currentIdx = bands.indexOf(journeyData?.current_band || 'developing');
                          const nextBand = bands[currentIdx + 1];
                          const thresholds = { secure: 50, proficient: 70, advanced: 85 };
                          const lastScore = journeyData?.last_adaptive_mock_score || 0;
                          const needed = thresholds[nextBand] - lastScore;
                          return needed > 0 
                            ? `${Math.ceil(needed)}% more to reach ${nextBand.charAt(0).toUpperCase() + nextBand.slice(1)}`
                            : `Almost at ${nextBand.charAt(0).toUpperCase() + nextBand.slice(1)}!`;
                        })()}
                      </span>
                      <ArrowRight className="h-4 w-4 text-purple-400" />
                    </div>
                  </div>
                )}
                
                {journeyData?.current_band === 'advanced' && (
                  <div className="mt-3 p-2 bg-purple-500/20 rounded-lg border border-purple-500/30">
                    <div className="flex items-center gap-2 text-sm text-purple-300">
                      <Crown className="h-4 w-4" />
                      <span>You've reached the highest band! Keep practicing to maintain mastery.</span>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Progress Stats Grid - Mobile Responsive */}
          <div className="grid grid-cols-3 gap-2 sm:gap-4">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-2 sm:p-4 text-center">
                <p className="text-xl sm:text-3xl font-bold text-purple-400">{weekProgress}%</p>
                <p className="text-gray-400 text-[10px] sm:text-sm">Week Progress</p>
              </CardContent>
            </Card>
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-2 sm:p-4 text-center">
                <p className="text-xl sm:text-3xl font-bold text-green-400">{gamificationProfile?.stats?.lessons_completed || 0}</p>
                <p className="text-gray-400 text-[10px] sm:text-sm">Lessons Done</p>
              </CardContent>
            </Card>
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-2 sm:p-4 text-center">
                <p className="text-xl sm:text-3xl font-bold text-pink-400">{xp.this_week}</p>
                <p className="text-gray-400 text-[10px] sm:text-sm">XP This Week</p>
              </CardContent>
            </Card>
          </div>

          {/* Weekly Assessment Banner (Weekend Only) */}
          {showAssessment && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Card className="bg-gradient-to-r from-purple-600 to-pink-600 border-0">
                <CardContent className="p-6 flex flex-col md:flex-row items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-white/20 rounded-xl">
                      <Target className="h-8 w-8 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white">Weekly Assessment Ready!</h3>
                      <p className="text-white/80">Complete your adaptive assessment to unlock next week's plan</p>
                    </div>
                  </div>
                  <Button
                    onClick={startWeeklyAssessment}
                    className="bg-white text-purple-600 hover:bg-gray-100"
                    data-testid="start-assessment-btn"
                  >
                    <Play className="h-4 w-4 mr-2" /> Start Assessment
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Learning Loop Visual */}
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <RefreshCw className="h-5 w-5 text-purple-400" />
                Your Learning Loop
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-center gap-2 md:gap-4 py-4">
                {PHASES.map((p, idx) => {
                  const Icon = p.icon;
                  const isActive = phase === p.id;
                  return (
                    <div key={p.id} className="flex items-center">
                      <div className={`flex flex-col items-center ${isActive ? 'scale-110' : 'opacity-60'}`}>
                        <div className={`p-3 rounded-full ${isActive ? 'bg-purple-600' : 'bg-slate-700'}`}>
                          <Icon className={`h-5 w-5 ${isActive ? 'text-white' : 'text-gray-400'}`} />
                        </div>
                        <span className={`text-xs mt-2 ${isActive ? 'text-purple-400' : 'text-gray-500'}`}>
                          {p.name}
                        </span>
                      </div>
                      {idx < PHASES.length - 1 && (
                        <ArrowRight className="h-4 w-4 text-gray-600 mx-1 md:mx-2" />
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Weekly Plan */}
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-white flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-blue-400" />
                  This Week's Plan
                </CardTitle>
                <span className="text-gray-400 text-sm">{weekProgress}% complete</span>
              </div>
            </CardHeader>
            <CardContent>
              {/* Day Tabs - Mobile Scrollable */}
              <Tabs value={selectedDay} onValueChange={setSelectedDay}>
                <div className="overflow-x-auto -mx-2 px-2 pb-2 scrollbar-hide" style={{ WebkitOverflowScrolling: 'touch' }}>
                  <TabsList className="bg-slate-700/50 inline-flex min-w-max sm:w-full sm:grid sm:grid-cols-5 mb-4">
                    {days.map(day => (
                      <TabsTrigger 
                        key={day} 
                        value={day}
                        className="data-[state=active]:bg-purple-600 text-xs px-4 sm:px-2 capitalize whitespace-nowrap"
                      >
                        {day.slice(0, 3)}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                </div>

                {days.map(day => {
                  const dayPlan = weeklyPlan?.find(d => d.day.toLowerCase() === day);
                  return (
                    <TabsContent key={day} value={day}>
                      <div className="space-y-3">
                        {dayPlan?.activities?.length > 0 ? (
                          dayPlan.activities.map((activity, idx) => (
                            <div
                              key={idx}
                              className={`flex flex-col sm:flex-row sm:items-center justify-between p-3 sm:p-4 rounded-lg border gap-3 ${
                                activity.completed 
                                  ? 'bg-green-500/10 border-green-500/30' 
                                  : 'bg-slate-700/30 border-slate-600'
                              }`}
                            >
                              <div className="flex items-start sm:items-center gap-3">
                                <div className={`p-2 rounded-lg flex-shrink-0 ${
                                  activity.completed ? 'bg-green-500/20' : 'bg-purple-500/20'
                                }`}>
                                  {activity.type === 'learn' && <BookOpen className="h-4 w-4 sm:h-5 sm:w-5 text-purple-400" />}
                                  {activity.type === 'practice' && <PenTool className="h-4 w-4 sm:h-5 sm:w-5 text-blue-400" />}
                                  {activity.type === 'review' && <RefreshCw className="h-4 w-4 sm:h-5 sm:w-5 text-green-400" />}
                                </div>
                                <div className="min-w-0">
                                  <p className="text-white font-medium text-sm sm:text-base truncate">{activity.title}</p>
                                  <p className="text-gray-400 text-xs sm:text-sm line-clamp-1">{activity.description}</p>
                                </div>
                              </div>
                              <div className="flex items-center justify-between sm:justify-end gap-3 pl-11 sm:pl-0">
                                <span className="text-gray-400 text-xs sm:text-sm">{activity.estimated_time} min</span>
                                {activity.completed ? (
                                  <CheckCircle2 className="h-5 w-5 text-green-400 flex-shrink-0" />
                                ) : (
                                  <Button
                                    size="sm"
                                    onClick={() => openLesson(activity.lesson_id)}
                                    className="bg-purple-600 hover:bg-purple-700 text-xs h-8"
                                    data-testid={`start-activity-${idx}`}
                                  >
                                    <Play className="h-3 w-3 sm:h-4 sm:w-4" />
                                  </Button>
                                )}
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="text-center py-8 text-gray-500">
                            No activities scheduled for this day
                          </div>
                        )}
                      </div>
                    </TabsContent>
                  );
                })}
              </Tabs>
            </CardContent>
          </Card>

          {/* Quick Actions - Assessment only */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Button
              variant="outline"
              onClick={startWeeklyAssessment}
              disabled={loading}
              className="h-auto py-4 flex-col gap-2 border-orange-500/50 hover:border-orange-500 hover:bg-orange-500/10 bg-orange-500/5"
              data-testid="quick-assessment-btn"
            >
              <Target className="h-6 w-6 text-orange-400" />
              <span className="text-white text-sm">Take Weekly Assessment</span>
              <span className="text-gray-400 text-xs">15 questions • Adaptive difficulty</span>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ============================================
  // RENDER: Phase 3 - Lesson View
  // ============================================
  if (view === 'lesson' && currentLesson) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-4 md:p-8" data-testid="lesson-view">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <Button variant="ghost" onClick={() => setView('dashboard')} className="text-gray-400 hover:text-white">
              <ChevronLeft className="h-4 w-4 mr-2" /> Back to Dashboard
            </Button>
            <Badge className="bg-purple-500/20 text-purple-300">{currentLesson.topic}</Badge>
          </div>

          <Card className="bg-slate-800/70 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white text-2xl">{currentLesson.title}</CardTitle>
              <CardDescription className="text-gray-400">{currentLesson.description}</CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs value={lessonTab} onValueChange={setLessonTab}>
                <TabsList className="bg-slate-700/50 w-full grid grid-cols-3 mb-6">
                  <TabsTrigger value="learn" className="data-[state=active]:bg-purple-600">
                    <BookOpen className="h-4 w-4 mr-2" /> Learn
                  </TabsTrigger>
                  <TabsTrigger value="practice" className="data-[state=active]:bg-purple-600">
                    <PenTool className="h-4 w-4 mr-2" /> Practice
                  </TabsTrigger>
                  <TabsTrigger value="tips" className="data-[state=active]:bg-purple-600">
                    <Lightbulb className="h-4 w-4 mr-2" /> Tips
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="learn">
                  <div className="prose prose-invert max-w-none">
                    <div className="text-gray-300 space-y-4">
                      {currentLesson.content?.explanation || 'Loading content...'}
                    </div>
                    {currentLesson.content?.examples && (
                      <div className="mt-6 p-4 bg-slate-700/30 rounded-lg">
                        <h4 className="text-purple-400 font-semibold mb-2">Example</h4>
                        <p className="text-gray-300">{currentLesson.content.examples[0]}</p>
                      </div>
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="practice">
                  <div className="space-y-6">
                    {currentLesson.practice_questions?.map((q, idx) => (
                      <div key={idx} className="p-4 bg-slate-700/30 rounded-lg">
                        <p className="text-white font-medium mb-4">{q.question}</p>
                        <RadioGroup
                          value={practiceAnswers[idx]?.toString()}
                          onValueChange={(v) => setPracticeAnswers(prev => ({...prev, [idx]: parseInt(v)}))}
                          disabled={practiceSubmitted}
                        >
                          {q.options?.map((opt, optIdx) => (
                            <div
                              key={optIdx}
                              className={`flex items-center space-x-3 p-3 rounded-lg border ${
                                practiceSubmitted
                                  ? optIdx === q.correct_answer
                                    ? 'border-green-500 bg-green-500/10'
                                    : practiceAnswers[idx] === optIdx
                                    ? 'border-red-500 bg-red-500/10'
                                    : 'border-slate-600'
                                  : practiceAnswers[idx] === optIdx
                                  ? 'border-purple-500 bg-purple-500/10'
                                  : 'border-slate-600'
                              }`}
                            >
                              <RadioGroupItem value={optIdx.toString()} id={`q${idx}-opt${optIdx}`} />
                              <Label htmlFor={`q${idx}-opt${optIdx}`} className="text-gray-300 cursor-pointer">
                                {opt}
                              </Label>
                            </div>
                          ))}
                        </RadioGroup>
                      </div>
                    ))}
                    
                    {!practiceSubmitted ? (
                      <Button
                        onClick={submitPractice}
                        disabled={Object.keys(practiceAnswers).length < (currentLesson.practice_questions?.length || 0)}
                        className="w-full bg-gradient-to-r from-purple-600 to-pink-600"
                      >
                        Check Answers
                      </Button>
                    ) : (
                      <Button
                        onClick={() => setView('dashboard')}
                        className="w-full bg-gradient-to-r from-green-600 to-emerald-600"
                      >
                        <CheckCircle2 className="h-4 w-4 mr-2" /> Complete & Continue
                      </Button>
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="tips">
                  <div className="space-y-4">
                    {(currentLesson.content?.tips || ['Focus on understanding the concept', 'Practice with different examples']).map((tip, idx) => (
                      <div key={idx} className="flex items-start gap-3 p-4 bg-slate-700/30 rounded-lg">
                        <Lightbulb className="h-5 w-5 text-yellow-400 mt-0.5" />
                        <p className="text-gray-300">{tip}</p>
                      </div>
                    ))}
                  </div>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // ============================================
  // RENDER: Phase 4 - Weekly Assessment
  // ============================================
  if (view === 'assessment' && assessmentData) {
    const currentQ = assessmentData.questions[assessmentIndex];
    const progress = ((assessmentIndex + 1) / assessmentData.questions.length) * 100;
    
    return (
      <div className="min-h-screen bg-slate-900" data-testid="assessment-view">
        {/* Timer Header */}
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 py-3 px-4">
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            <Badge className="bg-white/20 text-white">
              Question {assessmentIndex + 1} of {assessmentData.questions.length}
            </Badge>
            <div className="flex items-center gap-2 px-4 py-2 bg-white/10 rounded-lg">
              <Clock className="h-5 w-5 text-white" />
              <span className="font-mono text-white">
                {Math.floor(assessmentTime / 60)}:{(assessmentTime % 60).toString().padStart(2, '0')}
              </span>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 py-6">
          <Progress value={progress} className="h-2 bg-slate-700 mb-6" />

          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <div className="flex items-center justify-between">
                <Badge variant="outline" className="text-gray-400 border-gray-600">
                  {currentQ?.zone?.replace('_', ' ')}
                </Badge>
                {currentQ?.is_ai_generated && (
                  <Badge className="bg-pink-500/20 text-pink-400">
                    <Sparkles className="h-3 w-3 mr-1" /> AI Generated
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <p className="text-xl text-white leading-relaxed">{currentQ?.question_text}</p>

              <div className="space-y-3">
                {currentQ?.options?.map((option, idx) => (
                  <button
                    key={idx}
                    onClick={() => setAssessmentAnswers(prev => ({...prev, [currentQ.question_id]: idx}))}
                    className={`w-full p-4 rounded-lg text-left transition-all ${
                      assessmentAnswers[currentQ.question_id] === idx
                        ? 'bg-purple-600 border-purple-500 text-white'
                        : 'bg-slate-700/50 border-slate-600 text-gray-300 hover:bg-slate-700'
                    } border`}
                  >
                    <span className="font-medium mr-3">{String.fromCharCode(65 + idx)}.</span>
                    {option}
                  </button>
                ))}
              </div>

              <div className="flex justify-between pt-4">
                <Button
                  variant="outline"
                  onClick={() => setAssessmentIndex(prev => Math.max(0, prev - 1))}
                  disabled={assessmentIndex === 0}
                  className="border-slate-600 text-gray-300"
                >
                  <ChevronLeft className="h-4 w-4 mr-2" /> Previous
                </Button>
                
                {assessmentIndex === assessmentData.questions.length - 1 ? (
                  <Button
                    onClick={submitAssessment}
                    className="bg-gradient-to-r from-green-600 to-emerald-600"
                    disabled={loading}
                  >
                    {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                    Submit Assessment
                  </Button>
                ) : (
                  <Button
                    onClick={() => setAssessmentIndex(prev => prev + 1)}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    Next <ChevronRight className="h-4 w-4 ml-2" />
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // ============================================
  // RENDER: Phase 4 - Assessment Results
  // ============================================
  if (view === 'assessment-results' && assessmentResults) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-4 md:p-8" data-testid="assessment-results-view">
        <div className="max-w-3xl mx-auto">
          {/* Score Header */}
          <div className="text-center mb-8">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 mb-4"
            >
              <span className="text-3xl font-bold text-white">{Math.round(assessmentResults.score)}%</span>
            </motion.div>
            <h1 className="text-3xl font-bold text-white mb-2">Assessment Complete!</h1>
            <p className="text-gray-400">
              {assessmentResults.correct_count}/{assessmentResults.total_questions} correct
            </p>
          </div>

          {/* Insights */}
          <div className="grid md:grid-cols-2 gap-4 mb-8">
            <Card className="bg-slate-800/70 border-slate-700">
              <CardHeader className="pb-2">
                <CardTitle className="text-green-400 text-lg">Strong Topics</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {assessmentResults.strong_topics?.slice(0, 3).map((topic, idx) => (
                    <Badge key={idx} className="bg-green-500/20 text-green-400">
                      {topic}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-800/70 border-slate-700">
              <CardHeader className="pb-2">
                <CardTitle className="text-orange-400 text-lg">Focus Areas</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {assessmentResults.weak_topics?.slice(0, 3).map((topic, idx) => (
                    <Badge key={idx} className="bg-orange-500/20 text-orange-400">
                      {topic}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Recommendations */}
          {assessmentResults.recommendations?.length > 0 && (
            <Card className="bg-slate-800/70 border-slate-700 mb-8">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Lightbulb className="h-5 w-5 text-yellow-400" />
                  Ed's Picks
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {assessmentResults.recommendations.slice(0, 3).map((rec, idx) => (
                  <div key={idx} className="flex items-start gap-3 p-3 bg-slate-700/30 rounded-lg">
                    <Zap className="h-5 w-5 text-purple-400 mt-0.5" />
                    <p className="text-gray-300">{rec.message}</p>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={generateNextWeek}
              className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
              disabled={loading}
            >
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
              Generate Next Week's Plan
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/categories')}
              className="border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10"
            >
              <Library className="h-4 w-4 mr-2" />
              Extra Practice
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Default loading
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-purple-400" />
    </div>
  );
};

export default StudentJourney;
